import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import ReactCountryFlag from 'react-country-flag'
import COUNTRIES_EN from 'world_countries_lists/data/countries/en/world.json'

import TranslationNS from '@/utils/Constants/TranslationNS'
import StatusDot from '@/components/common/StatusDot'
import FallbackImg from '@/components/common/FallbackImg'
import { getRegistrationStatusDisplay } from '@/components/common/StatusDot/index.utils'
import defaultLogo from '@/images/scoring_icon.svg'
import DumbbellSVG from '@/images/home/categories/Dumbbell.svg?react'
import RunningFigureSVG from '@/images/home/categories/RunningFigure.svg?react'
import GraduationCapSVG from '@/images/home/categories/GraduationCap.svg?react'
import { formatLocalDateRange } from '@/utils/DateHelpers'
import { GetEventLink } from '@/utils/CompetitionLinkHelpers'
import { DEFAULT_EVENT_BANNER } from '@/utils/eventBanner'

const CATEGORY_ICON = {
    'Functional Fitness': DumbbellSVG,
    'Hybrid Race': RunningFigureSVG,
    Workshop: GraduationCapSVG,
}

const CHIP =
    'inline-flex flex-none items-center gap-[calc(5*var(--u))] min-h-[calc(21*var(--u))] px-[calc(5*var(--u))] text-[calc(10*var(--u))] font-light leading-none text-white whitespace-nowrap bg-[rgba(104,104,104,0.28)] border border-white/50 rounded-[5px] backdrop-blur-[6px]'

const EventCard = ({ tz, competition, registrationStatus }) => {
    const { t, i18n } = useTranslation(TranslationNS.HOME_PAGE)
    const { t: tMonths } = useTranslation(TranslationNS.MONTHS)

    const [code, setCode] = useState()

    const { location, country, category } = competition._event
    const eventLink = GetEventLink(competition)
    const isOnline = competition.type === 'online'
    const placeLabel = country || location || ''

    useEffect(() => {
        if (country) {
            const countryFound = COUNTRIES_EN.find((item) => item.name === country)

            if (countryFound) {
                setCode(countryFound.alpha2)
            }
        }
    }, [country])

    // Localized ("Jun 7 – 8, 2026" / "7–8 juin 2026"), in the viewer's timezone like the rest of the card.
    const dateString = useMemo(
        () =>
            formatLocalDateRange(competition.date?.start?.iso, competition.date?.end?.iso, {
                language: i18n.resolvedLanguage,
                tz,
            }),
        [competition?.date, i18n.resolvedLanguage, tz],
    )

    const status = getRegistrationStatusDisplay(registrationStatus, t, tMonths, tz)

    const CategoryIcon = CATEGORY_ICON[competition._event?.category]

    return (
        <Link
            to={eventLink}
            className="group/event-card @container relative isolate block aspect-395/230 w-full overflow-hidden rounded-[10px] border border-[#dbdadf] text-white no-underline transition-all ease-in-out hover:text-white"
            style={{ '--u': '0.25316cqw' }}
        >
            {/* Photo — custom illustration, or the Scoring.fit default banner when none is set / the link is dead */}
            <FallbackImg
                src={competition.illustrationLink}
                fallback={DEFAULT_EVENT_BANNER}
                alt=""
                loading="lazy"
                className="absolute inset-0 z-0 size-full bg-[#1a1a1a] object-cover transition-transform ease-in-out group-hover/event-card:scale-104"
            />

            {/* Progressive blur (net on top → 2px at bottom) */}
            <span
                aria-hidden
                className="pointer-events-none absolute inset-0 z-[1] backdrop-blur-[2px] [mask-image:linear-gradient(180deg,transparent_1%,rgba(0,0,0,0.5)_58%)] [-webkit-mask-image:linear-gradient(180deg,transparent_1%,rgba(0,0,0,0.5)_58%)]"
            />

            {/* Scrim: transparent → #272727 @ 50% from 58% */}
            <span
                aria-hidden
                className="pointer-events-none absolute inset-0 z-[2] bg-[linear-gradient(180deg,transparent_1%,rgba(39,39,39,0.5)_58%)]"
            />

            {/* Content — bottom-aligned, logo | text column */}
            <div
                className="absolute inset-0 z-10 flex flex-col justify-end"
                style={{
                    padding: 'calc(20 * var(--u)) calc(20 * var(--u)) calc(10 * var(--u)) calc(10 * var(--u))',
                }}
            >
                <div className="flex min-w-0 items-end gap-[calc(11*var(--u))]">
                    <FallbackImg
                        src={competition.iconLink}
                        fallback={defaultLogo}
                        loading="lazy"
                        alt={`logo of ${competition.name}`}
                        className="h-[calc(84*var(--u))] w-[calc(84*var(--u))] flex-none rounded-[10px] border border-[#dbdadf] bg-[#1a1a1a] object-cover object-center"
                        fallbackClassName="bg-white"
                    />

                    <div className="flex min-w-0 flex-1 flex-col gap-[calc(3*var(--u))]">
                        {/* Country chip + Online (if any) + date */}
                        <div className="flex min-h-[calc(21*var(--u))] flex-nowrap items-center gap-[calc(9*var(--u))]">
                            {placeLabel && (
                                <span className={CHIP}>
                                    {country && code && (
                                        <ReactCountryFlag
                                            countryCode={code}
                                            svg
                                            className="!h-[calc(12*var(--u))] !w-[calc(18*var(--u))] flex-none rounded-[1px]"
                                            title={code}
                                        />
                                    )}
                                    <span>{placeLabel}</span>
                                </span>
                            )}
                            {isOnline && <span className={CHIP}>{t('Online')}</span>}
                            <span className={CHIP}>{competition.formattedDate || dateString}</span>
                        </div>

                        <h3 className="m-0 line-clamp-2 overflow-hidden font-display text-[calc(18*var(--u))] font-bold leading-[calc(7/6)] tracking-[-0.01em] text-white uppercase">
                            {competition.name}
                        </h3>

                        {/* Category ↔ status — no hairline, no separators */}
                        <div className="font-meta flex min-h-[calc(17*var(--u))] items-center justify-between gap-[calc(8*var(--u))] text-[calc(10*var(--u))] font-light text-white">
                            <span className="inline-flex flex-none items-center gap-[calc(6*var(--u))]">
                                {CategoryIcon && <CategoryIcon className="h-[calc(11*var(--u))] w-auto flex-none" />}
                                {category && <span>{category}</span>}
                            </span>
                            <span className="inline-flex flex-none items-center gap-[calc(5*var(--u))]">
                                <StatusDot
                                    color={status.color}
                                    className="h-[calc(17*var(--u))] w-[calc(17*var(--u))]"
                                />
                                <span>{status.label}</span>
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </Link>
    )
}

export default EventCard
